import React, {useState, useRef} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {useApp} from '../context/AppContext';

const {FiUser, FiMail, FiBook, FiMapPin, FiSave, FiEdit3, FiCamera, FiTrash2, FiPhone, FiBriefcase} = FiIcons;

const Profile = ({user, onUpdateProfile, onClose}) => {
  const {state, authService, dispatch} = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: state.profile?.name || user?.name || '',
    email: state.profile?.email || user?.email || '',
    faculty: state.profile?.faculty || user?.faculty || '',
    department: state.profile?.department || user?.department || '',
    position: state.profile?.position || user?.position || '',
    phone: state.profile?.phone || user?.phone || '',
    office: state.profile?.office || user?.office || '',
    profile_picture: state.profile?.profile_picture || null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const {data, error} = await authService.updateProfile(state.user.id, formData);
      if (error) throw new Error(error);

      // Update global state
      dispatch({type: 'SET_PROFILE', payload: data});
      
      if (onUpdateProfile) {
        await onUpdateProfile(formData);
      }
      
      setIsEditing(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Profile update error:', error);
      alert('Error updating profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const {name, value} = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert image to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setFormData(prev => ({...prev, profile_picture: imageData}));
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert('Error uploading image: ' + error.message);
      setUploadingImage(false);
    }
  };

  const removeProfilePicture = () => {
    setFormData(prev => ({...prev, profile_picture: null}));
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const getInitials = () => {
    const name = formData.name || 'User';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="space-y-6"
    >
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
          <div className="flex items-center space-x-3">
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                <span>Edit Profile</span>
              </button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>

        {/* Profile Picture Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            {formData.profile_picture ? (
              <img
                src={formData.profile_picture}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-gray-200 shadow-lg"
              />
            ) : (
              <div className="w-32 h-32 bg-blue-600 rounded-full flex items-center justify-center border-4 border-gray-200 shadow-lg">
                <span className="text-white text-3xl font-medium">
                  {getInitials()}
                </span>
              </div>
            )}
            
            {uploadingImage && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {isEditing && (
              <div className="absolute -bottom-2 -right-2 flex space-x-2">
                <button
                  onClick={triggerImageUpload}
                  disabled={uploadingImage}
                  className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-lg"
                  title="Change profile picture"
                >
                  <SafeIcon icon={FiCamera} className="w-5 h-5 text-white" />
                </button>
                {formData.profile_picture && (
                  <button
                    onClick={removeProfilePicture}
                    className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
                    title="Remove profile picture"
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>
            )}
          </div>
          
          {!isEditing && (
            <div className="text-center mt-4">
              <h3 className="text-xl font-semibold text-gray-900">{formData.name}</h3>
              <p className="text-gray-600">{formData.position}</p>
              <p className="text-sm text-gray-500">{formData.email}</p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Enter your full name"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Faculty
              </label>
              <div className="relative">
                <SafeIcon icon={FiBook} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="faculty"
                  value={formData.faculty}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Faculty of Engineering"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <div className="relative">
                <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Computer Science"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position
              </label>
              <div className="relative">
                <SafeIcon icon={FiBriefcase} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="Senior Lecturer"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                  placeholder="+60 12-345 6789"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Office Location
            </label>
            <div className="relative">
              <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="office"
                value={formData.office}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Room 301, Block A"
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  // Reset form data to original values
                  setFormData({
                    name: state.profile?.name || user?.name || '',
                    email: state.profile?.email || user?.email || '',
                    faculty: state.profile?.faculty || user?.faculty || '',
                    department: state.profile?.department || user?.department || '',
                    position: state.profile?.position || user?.position || '',
                    phone: state.profile?.phone || user?.phone || '',
                    office: state.profile?.office || user?.office || '',
                    profile_picture: state.profile?.profile_picture || null
                  });
                }}
                disabled={loading}
                className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                )}
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          )}
        </form>

        {/* Profile Statistics */}
        {!isEditing && (
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Profile Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <p className="font-medium text-gray-900">{state.courses?.length || 0}</p>
                <p className="text-gray-600">Total Courses</p>
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-900">
                  {state.courses?.filter(c => c.status === 'completed')?.length || 0}
                </p>
                <p className="text-gray-600">Completed</p>
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-900">
                  {state.courses?.filter(c => c.status === 'finalized')?.length || 0}
                </p>
                <p className="text-gray-600">Finalized</p>
              </div>
              <div className="text-center">
                <p className="font-medium text-gray-900">
                  {state.archivedReports?.length || 0}
                </p>
                <p className="text-gray-600">Archived Reports</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
    </motion.div>
  );
};

export default Profile;